import { X, Calendar } from 'lucide-react';

export interface AvailabilityExceptionItem {
  exceptionId?: number | string;
  startDate?: string | null;
  endDate?: string | null;
  availableHours?: number | string | null;
  reason?: string | null;
  [key: string]: unknown;
}

interface AvailabilityExceptionsModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  exceptions: AvailabilityExceptionItem[];
}

export function formatExceptionDate(dateValue?: string | null) {
  if (!dateValue) {
    return null;
  }

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
}

export default function AvailabilityExceptionsModal({
  open,
  onClose,
  title = 'Upcoming Availability Changes',
  subtitle,
  exceptions,
}: AvailabilityExceptionsModalProps) {
  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/40 px-4 py-6"
      onClick={onClose}
    >
      <div
        className="flex h-auto max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {subtitle ? <p className="text-sm text-gray-500">{subtitle}</p> : null}
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {exceptions.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">No upcoming changes.</p>
          ) : (
            <ul className="space-y-3">
              {exceptions.map((exception, index) => {
                const fromDate = formatExceptionDate(exception.startDate);
                const toDate = formatExceptionDate(exception.endDate);
                const hours = Number(exception.availableHours ?? 0);
                const isAvailable = hours > 0;

                return (
                  <li
                    key={exception.exceptionId ?? index}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        {fromDate ? fromDate : 'Date TBD'}
                        {toDate && toDate !== fromDate ? ` – ${toDate}` : ''}
                      </div>
                      <span
                        className={`rounded-md px-2 py-0.5 text-xs font-semibold ${
                          isAvailable ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}
                      >
                        {isAvailable ? 'Available' : 'Unavailable'}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <span className="rounded-md bg-white px-2 py-0.5 text-xs font-medium text-gray-700 shadow-sm">
                        {hours} hrs/week
                      </span>
                    </div>
                    {exception.reason ? <p className="mt-2 text-xs text-gray-500">{exception.reason}</p> : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
